import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServiceWorkerUpdaterProps, withServiceWorkerUpdater } from '@3m1/service-worker-updater';

import { UserJson } from '../../../types/models';
import { useNavbar } from '../../../hooks/use-navbar';
import { useAuth } from '../../../hooks/use-auth';
import { useTheme } from '../../../hooks/use-theme';
import { faroLogger } from '../../../observability/faro-logger';

const AVATAR_COLORS = [
  '#1a6fa8', '#2a7a5c', '#7a4a2a', '#5a2a7a',
  '#1a5a8a', '#8a2a2a', '#2a5a4a', '#6a3a6a',
];

function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const Icon: FC<{ name: string; size?: number; color?: string }> = ({
  name,
  size = 18,
  color = 'currentColor',
}) => {
  const p = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
  case 'help':
    return (
      <svg {...p}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  case 'logout':
    return (
      <svg {...p}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    );
  case 'chevron-right':
    return (
      <svg {...p} strokeWidth={2.4}>
        <polyline points="9 18 15 12 9 6" />
      </svg>
    );
  default:
    return null;
  }
};

const Account = (props: ServiceWorkerUpdaterProps): JSX.Element => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const auth = useAuth();
  const { onLoadNewServiceWorkerAccept } = props;
  const user = (auth?.user || undefined) as UserJson | undefined;
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (navbar) {
      navbar.setTitle('Mon compte');
      navbar.setBackArrow(false);
      navbar.setRight();
    }
  }, [navbar]);

  if (!user) {
    return <></>;
  }

  const fullName = user.title || user.lastname || user.email || '';
  const initials = getInitials(fullName);
  const avatarColor = getAvatarColor(fullName);

  return (
    <div
      style={{
        padding: '12px 14px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* ===== Hero profil ===== */}
      <div
        style={{
          background:
            'linear-gradient(135deg, rgba(49,220,200,0.08), rgba(49,220,200,0.02))',
          border: '1px solid rgba(49,220,200,0.25)',
          borderRadius: 18,
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 140,
            height: 140,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(49,220,200,0.18), transparent 70%)',
          }}
        />
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: avatarColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-primary)',
            fontSize: 19,
            fontWeight: 900,
            letterSpacing: '0.04em',
            flexShrink: 0,
            boxShadow:
              `0 0 0 3px rgba(49,220,200,0.3), 0 8px 24px ${avatarColor}66`,
            position: 'relative',
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--teal)',
              marginBottom: 2,
            }}
          >
            Agent · Camion Net
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 900,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              textTransform: 'capitalize',
              wordBreak: 'break-word',
            }}
          >
            {fullName.toLowerCase()}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              marginTop: 2,
              wordBreak: 'break-all',
            }}
          >
            {user.email}
          </div>
        </div>
      </div>

      {/* ===== Toggle thème clair / sombre ===== */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 14,
          padding: '14px',
          marginTop: 4,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          Affichage
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => {
              faroLogger.info('theme_changed', { theme: 'dark' });
              setTheme('dark');
            }}
            style={{
              appearance: 'none',
              background: theme === 'dark' ? 'var(--teal)' : 'transparent',
              border: `1.5px solid ${
                theme === 'dark' ? 'var(--teal)' : 'rgba(255,255,255,0.18)'
              }`,
              borderRadius: 12,
              padding: '12px 10px',
              color: theme === 'dark' ? 'var(--navy)' : 'var(--text-primary)',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              WebkitTapHighlightColor: 'transparent',
              transition: 'all 0.15s ease',
            }}
          >
            🌙 Sombre
          </button>
          <button
            type="button"
            onClick={() => {
              faroLogger.info('theme_changed', { theme: 'light' });
              setTheme('light');
            }}
            style={{
              appearance: 'none',
              background: theme === 'light' ? 'var(--teal)' : 'transparent',
              border: `1.5px solid ${
                theme === 'light' ? 'var(--teal)' : 'rgba(255,255,255,0.18)'
              }`,
              borderRadius: 12,
              padding: '12px 10px',
              color: theme === 'light' ? 'var(--navy)' : 'var(--text-primary)',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              WebkitTapHighlightColor: 'transparent',
              transition: 'all 0.15s ease',
            }}
          >
            ☀️ Clair
          </button>
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            marginTop: 8,
            lineHeight: 1.4,
          }}
        >
          Le mode clair est conseillé en extérieur — meilleur contraste au
          soleil.
        </div>
      </div>

      {/* ===== Action principale : tutoriel ===== */}
      <button
        type="button"
        onClick={() => navigate('/demo')}
        style={{
          appearance: 'none',
          background: 'rgba(49,220,200,0.06)',
          border: '1px solid rgba(49,220,200,0.3)',
          borderRadius: 14,
          padding: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          width: '100%',
          marginTop: 4,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            background: 'rgba(49,220,200,0.16)',
            color: 'var(--teal)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="help" size={20} color="var(--teal)" />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.005em',
              lineHeight: 1.2,
            }}
          >
            Revoir le tutoriel
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--text-muted)',
              lineHeight: 1.3,
              marginTop: 2,
            }}
          >
            Comment ça marche, étape par étape
          </div>
        </span>
        <span style={{ flexShrink: 0, color: 'var(--teal)' }}>
          <Icon name="chevron-right" size={18} color="var(--teal)" />
        </span>
      </button>

      {/* ===== Déconnexion (action terminale, discrète) ===== */}
      <button
        type="button"
        onClick={async () => {
          await auth.signout();
          navigate('/');
        }}
        style={{
          appearance: 'none',
          background: 'transparent',
          border: '1px solid rgba(230,57,70,0.25)',
          borderRadius: 12,
          padding: '11px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          cursor: 'pointer',
          color: '#FF8A95',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.01em',
          width: '100%',
          marginTop: 2,
          WebkitTapHighlightColor: 'transparent',
          transition: 'background 0.15s ease',
        }}
      >
        <Icon name="logout" size={15} color="#FF8A95" />
        Se déconnecter
      </button>

      {/* ===== Footer version ===== */}
      <div
        style={{
          marginTop: 14,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-faint)',
          fontSize: 10.5,
        }}
      >
        <span>
          {process.env.REACT_APP_NAME} · v{process.env.REACT_APP_VERSION}
        </span>
        <button
          type="button"
          onClick={() => onLoadNewServiceWorkerAccept()}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: '1px solid rgba(248,250,251,0.15)',
            borderRadius: 100,
            padding: '4px 12px',
            color: 'var(--text-muted)',
            fontFamily: 'inherit',
            fontSize: 10.5,
            fontWeight: 600,
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          Forcer la mise à jour
        </button>
      </div>
    </div>
  );
};

export default withServiceWorkerUpdater(Account);
